import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { isoServices, trainingPrograms } from '../mockData';
import { getIcon } from '../utils/icons';
import { ArrowRight, CheckCircle, BookOpen } from 'lucide-react';

const ISODetailPage = () => {
  const { id } = useParams();
  const service = isoServices.find((s) => s.id === id);

  if (!service) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Standard Not Found</h1>
          <Link to="/certification">
            <Button className="bg-[#003f6d] hover:bg-[#002a4d] text-white">
              View All Standards
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = getIcon(service.icon);
  const relatedTraining = trainingPrograms.filter((t) => t.standard === service.code);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section
        className="py-20 text-white relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}dd 100%)`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-lg">
              <Icon className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-5xl font-bold">{service.code}</h1>
              <p className="text-2xl mt-2">{service.title}</p>
            </div>
          </div>
          <div className="w-20 h-1 bg-white mb-6"></div>
          <p className="text-xl max-w-3xl">{service.shortDescription}</p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {service.fullDescription}
              </p>
              <div className="bg-gray-50 border-l-4 p-6 rounded" style={{ borderColor: service.color }}>
                <h3 className="font-semibold text-gray-900 mb-2">Who Needs This Certification?</h3>
                <p className="text-gray-700">{service.whoNeedsIt}</p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NjkwMzI4MzV8MA&ixlib=rb-4.1.0&q=85"
                alt={service.title}
                className="rounded-lg shadow-xl w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Key Benefits of {service.code} Certification
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.benefits.map((benefit, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="w-6 h-6 flex-shrink-0 mt-1"
                      style={{ color: service.color }}
                    />
                    <p className="text-gray-700">{benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Programs */}
      {relatedTraining.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Related Training Programs
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedTraining.map((training, index) => (
                <Card key={index} className="hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <BookOpen className="w-6 h-6" style={{ color: service.color }} />
                      <span
                        className="text-sm font-semibold px-3 py-1 rounded-full"
                        style={{
                          backgroundColor: `${service.color}15`,
                          color: service.color
                        }}
                      >
                        {training.duration}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {training.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">{training.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">{training.level}</span>
                      <Link to="/training">
                        <Button variant="link" className="p-0" style={{ color: service.color }}>
                          Learn More
                          <ArrowRight className="ml-1 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section
        className="py-20 text-white"
        style={{
          background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}dd 100%)`
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Get {service.code} Certified?
          </h2>
          <p className="text-xl mb-8">
            Contact us today for a certification briefing and customized quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-gray-900 hover:bg-gray-100 font-semibold px-8 py-6 text-lg">
                Request a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/certification">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                View All Standards
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ISODetailPage;
